import { useEffect, useMemo, useRef, useState } from 'react';
import {
  invoiceForCampaign,
  payeeSupportsChannel,
  railsForChannel,
  reconciledAmount,
  validateTarget,
} from '@/utils/payment';
import {
  countryByCode,
  countryByDialCode,
  defaultCountry,
  dialCodeForNumber,
  dialCodesForCurrency,
  operatorById,
  operatorsForCurrency,
  stripDial,
} from '@/utils/sendMoney';
import { bankCountries, moneyRecipients } from '@/mocks/sendMoney';
import { payoutRails } from '@/mocks/sendPayment';
import type {
  FundingWallet,
  MoneyRecipient,
  PayeeTarget,
  SinglePayee,
  SinglePayment,
  SinglePaymentChannel,
} from '@/types/portal';
import type { PaymentSentMeta } from '../types';
import ManageFavouritesStep from './ManageFavouritesStep';
import MobileAccountStep from './MobileAccountStep';
import OperatorStep from './OperatorStep';
import BankRecipientStep from './BankRecipientStep';
import BankPickerStep from './BankPickerStep';
import BankAccountStep from './BankAccountStep';
import WalletAccountStep from './WalletAccountStep';
import FlowDetailsStep from './FlowDetailsStep';
import { imageUrl } from '@/utils/assets';
import { api } from '@/utils/api';

const NEW_PAYEE_AVATAR = imageUrl('payments/new-payee-avatar.webp');

type FlowType = 'mobile' | 'bank' | 'wallet';

type MobileSubStep = 'account' | 'operator' | 'details' | 'favourites';
type BankSubStep = 'recipient' | 'bank' | 'account' | 'details';
type WalletSubStep = 'account' | 'details';

interface SendMoneySheetProps {
  channel: FlowType;
  wallets: FundingWallet[];
  payees: SinglePayee[];
  canManage: boolean;
  canApprove: boolean;
  counter: number;
  onPaymentSent: (payment: SinglePayment, meta: PaymentSentMeta) => void;
  onClose: () => void;
  /** A payee to preselect, e.g. from a "Pay creator" deep link. */
  initialPayeeId?: string | null;
}

/**
 * Multi-step "Send money" sheet. The host page handles open/close state and
 * passes wallets, payees and permissions; this component owns the step stack.
 */
export default function SendMoneySheet({
  channel,
  wallets,
  payees: savedPayees,
  canManage,
  canApprove,
  counter,
  onPaymentSent,
  onClose,
  initialPayeeId = null,
}: SendMoneySheetProps) {
  // ---- favourites state (lifted so toggling persists during the session) ----
  const initialFavIds = useRef(new Set(moneyRecipients.filter((r) => r.favourite).map((r) => r.id)));
  const [favouriteIds, setFavouriteIds] = useState<Set<string>>(initialFavIds.current);
  // Extra recipients added via "Add a new number" in the Manage screen
  const [extraRecipients, setExtraRecipients] = useState<typeof moneyRecipients>([]);

  const allRecipients = useMemo(
    () => [...moneyRecipients, ...extraRecipients],
    [extraRecipients],
  );

  const favourites = useMemo(
    () => allRecipients.filter((r) => favouriteIds.has(r.id)),
    [allRecipients, favouriteIds],
  );

  const handleToggleFavourite = (id: string) => {
    setFavouriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddNumber = (digits: string, dc: string) => {
    const fullNumber = `${dc.replace(/\D/g, '')}${digits}`;
    const existing = allRecipients.find((r) => r.number === fullNumber);
    if (existing) {
      setFavouriteIds((prev) => new Set([...prev, existing.id]));
      return;
    }
    const id = `mr-new-${Date.now()}`;
    const newRecipient = {
      id,
      name: `${dc} ${digits}`,
      avatar: NEW_PAYEE_AVATAR,
      countryCode: '',
      number: fullNumber,
      operatorId: '',
      favourite: true,
    };
    setExtraRecipients((prev) => [...prev, newRecipient]);
    setFavouriteIds((prev) => new Set([...prev, id]));
  };

  // ---- mobile sub-step state ----
  const dialCodesRaw = useMemo(
    () =>
      [...new Set(bankCountries.filter((c) => c.currency !== '').map((c) => c.dialCode))].slice(0, 8),
    [],
  );
  const [mobileSubStep, setMobileSubStep] = useState<MobileSubStep>('account');
  const [dialCode, setDialCode] = useState(dialCodesRaw[0] ?? '+254');
  const [localNumber, setLocalNumber] = useState('');
  const [operatorId, setOperatorId] = useState<string | null>(null);

  // ---- bank sub-step state ----
  const [bankSubStep, setBankSubStep] = useState<BankSubStep>('recipient');
  const [bankMethod, setBankMethod] = useState<'account' | 'phone' | null>(null);
  const [countryCode, setCountryCode] = useState<string>('KE');
  const [selectedBankName, setSelectedBankName] = useState<string | null>(null);
  const [bankAccountNumber, setBankAccountNumber] = useState('');

  // ---- wallet sub-step state ----
  const [walletSubStep, setWalletSubStep] = useState<WalletSubStep>('account');
  const [walletValue, setWalletValue] = useState('');
  const [walletChecking, setWalletChecking] = useState(false);
  const [walletError, setWalletError] = useState('');

  // ---- shared payment details state ----
  const [selectedPayeeId, setSelectedPayeeId] = useState<string | null>(null);
  const [isManualRecipient, setIsManualRecipient] = useState(false);
  const [manualRecipientName, setManualRecipientName] = useState('');
  const [manualRecipientAvatar, setManualRecipientAvatar] = useState(NEW_PAYEE_AVATAR);
  const [walletId, setWalletId] = useState<string>(wallets[0]?.id ?? '');
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [campaignId, setCampaignId] = useState<string | null>(null);

  // ---- derived state ----
  const operators = useMemo(() => {
    const wallet = wallets.find((w) => w.id === walletId);
    const currency = wallet?.currency ?? '';
    return operatorsForCurrency(currency);
  }, [wallets, walletId]);

  const storedPayee = savedPayees.find((p) => p.id === selectedPayeeId) ?? null;
  const payee = storedPayee;

  const wallet = wallets.find((w) => w.id === walletId) ?? null;
  const currency = wallet?.currency ?? storedPayee?.currency ?? '';
  const money = currency || 'KES';

  const invoice = useMemo(
    () => invoiceForCampaign(storedPayee, campaignId),
    [storedPayee, campaignId],
  );

  // ---- target value for the chosen channel ----
  const targetValue = useMemo(() => {
    if (channel === 'mobile') return `${dialCode.replace(/\D/g, '')}${localNumber}`;
    if (channel === 'bank') return bankAccountNumber;
    return walletValue;
  }, [channel, dialCode, localNumber, bankAccountNumber, walletValue]);

  const targetLabel = useMemo(() => {
    if (channel === 'mobile') {
      const op = operatorById(operatorId ?? undefined);
      return `${op?.name ?? 'Mobile'} · ${dialCode} ${localNumber}`;
    }
    if (channel === 'bank') {
      const country = countryByCode(countryCode);
      return `${selectedBankName ?? ''} · ${bankAccountNumber.slice(-4) ? `••••${bankAccountNumber.slice(-4)}` : '—'} · ${country?.name ?? countryCode}`;
    }
    return `Wallet ${walletValue}`;
  }, [channel, dialCode, localNumber, operatorId, selectedBankName, bankAccountNumber, walletValue, countryCode]);

  const rail = useMemo(() => {
    return railsForChannel(payoutRails, channel as SinglePaymentChannel, money)[0] ?? null;
  }, [channel, money]);

  const fee = rail?.fee ?? 0;
  const totalDebit = amount + fee;
  const shortfall = wallet ? Math.max(0, totalDebit - wallet.balance) : 0;
  const covered = wallet !== null && shortfall <= 0;

  const validation = validateTarget(channel as SinglePaymentChannel, targetValue, currency || undefined);
  const targetError = targetValue && !validation.valid ? validation.error : '';

  const recipientName =
    isManualRecipient ? manualRecipientName || 'New recipient' : storedPayee?.name ?? 'Select recipient';
  const recipientAvatar =
    isManualRecipient ? manualRecipientAvatar : storedPayee?.avatar ?? NEW_PAYEE_AVATAR;

  const ready = useMemo(() => {
    if (!canManage) return false;
    if (!wallet) return false;
    if (amount <= 0) return false;
    if (!validation.valid) return false;
    if (!covered) return false;
    return true;
  }, [canManage, wallet, amount, validation.valid, covered]);

  // ---- helpers ----
  const pickRecipient = (recipient: MoneyRecipient) => {
    const matched = savedPayees.find((p) => p.target.mobileNumber === recipient.number);
    if (matched) {
      setSelectedPayeeId(matched.id);
      setIsManualRecipient(false);
      setCampaignId(matched.invoices[0]?.campaignId ?? null);
      setAmount(matched.invoices[0] ? reconciledAmount(matched.invoices[0]) : 0);
      const w = wallets.find((fw) => fw.currency === matched.currency);
      setWalletId(w?.id ?? '');
    } else {
      setIsManualRecipient(true);
      setManualRecipientName(recipient.name);
      setManualRecipientAvatar(recipient.avatar);
    }
    const dialFromNumber = dialCodeForNumber(recipient.number) ?? dialCode;
    setDialCode(dialFromNumber);
    setLocalNumber(stripDial(recipient.number, dialFromNumber));
    setOperatorId(recipient.operatorId);
    setMobileSubStep('details');
  };

  const pickPayee = (p: SinglePayee) => {
    setSelectedPayeeId(p.id);
    setIsManualRecipient(false);
    setCampaignId(p.invoices[0]?.campaignId ?? null);
    setAmount(p.invoices[0] ? reconciledAmount(p.invoices[0]) : 0);
    const w = wallets.find((fw) => fw.currency === p.currency);
    setWalletId(w?.id ?? '');
    if (channel === 'mobile' && p.target.mobileNumber) {
      const dc = dialCodeForNumber(p.target.mobileNumber) ?? dialCode;
      setDialCode(dc);
      setLocalNumber(stripDial(p.target.mobileNumber, dc));
      setMobileSubStep('details');
    } else if (channel === 'bank' && p.target.accountNumber) {
      setSelectedBankName(p.target.bankName ?? null);
      setBankAccountNumber(p.target.accountNumber);
      const country = defaultCountry(p.currency);
      setCountryCode(country.code);
      setBankSubStep('details');
    } else if (channel === 'wallet' && p.target.walletId) {
      setWalletValue(p.target.walletId);
      setWalletSubStep('details');
    }
  };

  const handleCampaign = (id: string) => {
    setCampaignId(id);
    const match = storedPayee?.invoices.find((inv) => inv.campaignId === id);
    if (match) setAmount(reconciledAmount(match));
  };

  const handleMobileContinue = () => {
    const fullNumber = `${dialCode.replace(/\D/g, '')}${localNumber}`;
    const match = savedPayees.find((p) => p.target.mobileNumber === fullNumber);
    if (match) {
      setSelectedPayeeId(match.id);
      setIsManualRecipient(false);
      setCampaignId(match.invoices[0]?.campaignId ?? null);
      setAmount(match.invoices[0] ? reconciledAmount(match.invoices[0]) : 0);
      const w = wallets.find((fw) => fw.currency === match.currency);
      setWalletId(w?.id ?? '');
    } else {
      setIsManualRecipient(true);
      setManualRecipientName('');
    }
    if (!walletId) {
      const defaultW = wallets[0];
      setWalletId(defaultW?.id ?? '');
    }
    if (operators.length > 1) {
      setMobileSubStep('operator');
    } else {
      if (operators.length === 1) setOperatorId(operators[0].id);
      setMobileSubStep('details');
    }
  };

  const handleOperatorSelect = (id: string) => {
    setOperatorId(id);
    setMobileSubStep('details');
  };

  const syncWalletToCurrency = (target: string) => {
    const match = wallets.find((fw) => fw.currency === target);
    if (match) setWalletId(match.id);
  };

  const handleBankMethod = (method: 'account' | 'phone') => {
    setBankMethod(method);
    const country = defaultCountry(wallet?.currency ?? 'KES');
    setCountryCode(country.code);
    syncWalletToCurrency(country.currency);
    setBankSubStep('bank');
  };

  const handleCountrySelect = (code: string) => {
    const country = countryByCode(code);
    setCountryCode(code);
    setSelectedBankName(null);
    if (country) syncWalletToCurrency(country.currency);
  };

  const handleBankContinue = () => {
    setBankSubStep('account');
  };

  const handleAccountContinue = () => {
    setBankSubStep('details');
  };

  // A wallet ID that isn't a saved payee is looked up first, so the recipient's real name shows.
  // A wallet found by ID in the search was already checked: go straight to the details.
  const pickWallet = (id: string, name: string) => {
    setWalletError('');
    setWalletValue(id);
    setManualRecipientName(name);
    setIsManualRecipient(true);
    if (!walletId) setWalletId(wallets[0]?.id ?? '');
    setWalletSubStep('details');
  };

  const handleWalletContinue = async () => {
    setWalletError('');
    const match = savedPayees.find((p) => p.target.walletId === walletValue);
    if (!match) {
      setWalletChecking(true);
      try {
        const info = await api<{ exists: boolean; name: string | null }>('/payments/resolve', {
          method: 'POST',
          body: JSON.stringify({ walletId: walletValue }),
        });
        if (!info.exists) {
          setWalletError(`No Twiva wallet has the ID ${walletValue}.`);
          return;
        }
        setManualRecipientName(info.name ?? '');
      } catch (error) {
        setWalletError(error instanceof Error ? error.message : 'Could not check the wallet ID.');
        return;
      } finally {
        setWalletChecking(false);
      }
    }
    if (match) {
      setSelectedPayeeId(match.id);
      setIsManualRecipient(false);
      setCampaignId(match.invoices[0]?.campaignId ?? null);
      setAmount(match.invoices[0] ? reconciledAmount(match.invoices[0]) : 0);
      const w = wallets.find((fw) => fw.currency === match.currency);
      setWalletId(w?.id ?? '');
    } else {
      setIsManualRecipient(true);
      if (!walletId) setWalletId(wallets[0]?.id ?? '');
    }
    setWalletSubStep('details');
  };

  const goBack = () => {
    if (channel === 'mobile') {
      if (mobileSubStep === 'favourites') setMobileSubStep('account');
      else if (mobileSubStep === 'operator') setMobileSubStep('account');
      else if (mobileSubStep === 'details') {
        if (operators.length > 1) setMobileSubStep('operator');
        else setMobileSubStep('account');
      }
    } else if (channel === 'bank') {
      if (bankSubStep === 'account') setBankSubStep('bank');
      else if (bankSubStep === 'bank') setBankSubStep('recipient');
      else if (bankSubStep === 'details') {
        setBankSubStep('account');
      }
    } else if (channel === 'wallet') {
      if (walletSubStep === 'details') setWalletSubStep('account');
    }
  };

  const channelTitle =
    channel === 'mobile'
      ? 'Send to mobile wallet'
      : channel === 'bank'
        ? 'Send to another bank'
        : 'Twiva Pay Wallet';

  const atFirstStep =
    (channel === 'mobile' && (mobileSubStep === 'account' || mobileSubStep === 'favourites')) ||
    (channel === 'bank' && bankSubStep === 'recipient') ||
    (channel === 'wallet' && walletSubStep === 'account');

  useEffect(() => {
    if (!initialPayeeId) return;
    const target = savedPayees.find((item) => item.id === initialPayeeId);
    if (!target) return;
    if (channel === 'mobile' && !target.target.mobileNumber) return;
    if (channel === 'bank' && !target.target.accountNumber) return;
    if (channel === 'wallet' && !target.target.walletId) return;
    pickPayee(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPayeeId, channel]);

  const buildNewPayee = (): SinglePayee => {
    const marketName =
      channel === 'bank'
        ? countryByCode(countryCode)?.name ?? 'Kenya'
        : countryByDialCode(dialCode)?.name ?? 'Kenya';
    const target: PayeeTarget = {};
    if (channel === 'mobile') {
      target.mobileNumber = `${dialCode.replace(/\D/g, '')}${localNumber}`;
      target.network = operatorById(operatorId ?? undefined)?.name;
    } else if (channel === 'bank') {
      target.accountNumber = bankAccountNumber;
      target.bankName = selectedBankName ?? undefined;
    } else {
      target.walletId = walletValue;
    }
    return {
      id: `pay-new-${Date.now()}`,
      kind: 'supplier',
      name: recipientName,
      avatar: recipientAvatar,
      market: marketName,
      currency: money,
      invoices: [],
      target,
      status: 'Verified',
    };
  };

  const handleSubmit = () => {
    if (!wallet || !rail) return;
    const status = canApprove ? 'Scheduled' : 'Awaiting approval';
    const payment: SinglePayment = {
      id: `sp-${counter}`,
      reference: `PAY-${counter}`,
      payeeName: recipientName,
      avatar: recipientAvatar,
      channel: channel as SinglePaymentChannel,
      target: targetLabel,
      campaign: invoice?.campaign,
      amount,
      currency: money,
      rail: `${rail.name} · ${money}`,
      status,
      createdAt: '16 Sep 2026',
      createdBy: 'Peter Kironji',
    };
    onPaymentSent(payment, {
      newPayee: isManualRecipient ? buildNewPayee() : null,
      request: {
        sourceWalletId: wallet.id,
        payeeId: storedPayee?.id ?? null,
        destination:
          channel === 'mobile'
            ? { number: targetValue }
            : channel === 'bank'
              ? { accountNumber: bankAccountNumber, bankName: selectedBankName ?? undefined }
              : { walletId: walletValue },
      },
    });
  };

  const walletPayees = useMemo(
    () => savedPayees.filter((p) => payeeSupportsChannel(p, 'wallet')),
    [savedPayees],
  );
  const mobilePayees = useMemo(
    () => savedPayees.filter((p) => payeeSupportsChannel(p, 'mobile')),
    [savedPayees],
  );
  const bankPayees = useMemo(
    () => savedPayees.filter((p) => payeeSupportsChannel(p, 'bank')),
    [savedPayees],
  );
  // favourites derived above from lifted state
  const mobileContinueReady = localNumber.length >= 7;

  const dialCodesForFlow = useMemo(() => dialCodesForCurrency(money), [money]);

  useEffect(() => {
    if (dialCodesForFlow.length > 0 && !dialCodesForFlow.includes(dialCode)) {
      setDialCode(dialCodesForFlow[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialCodesForFlow.join(',')]);

  return (
    <div>
      {/* Sheet header */}
      <div className="mb-6 flex items-center gap-3 border-b border-background-200 pb-4">
        {!atFirstStep ? (
          <button
            type="button"
            onClick={goBack}
            aria-label="Back"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-200"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
        ) : null}
        <h2 className="flex-1 font-heading text-base font-semibold text-foreground-950">
          {channelTitle}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {/* ---- Mobile money ---- */}
      {channel === 'mobile' ? (
        <>
          {mobileSubStep === 'account' ? (
            <MobileAccountStep
              dialCode={dialCode}
              dialCodes={dialCodesForFlow.length > 0 ? dialCodesForFlow : dialCodesRaw}
              onDialCode={setDialCode}
              number={localNumber}
              onNumber={setLocalNumber}
              recents={allRecipients}
              favourites={favourites}
              payees={mobilePayees}
              onPickRecipient={pickRecipient}
              onPickPayee={pickPayee}
              onContinue={handleMobileContinue}
              canContinue={mobileContinueReady}
              onManageFavourites={() => setMobileSubStep('favourites')}
            />
          ) : null}
          {mobileSubStep === 'favourites' ? (
            <ManageFavouritesStep
              allRecipients={allRecipients}
              favouriteIds={favouriteIds}
              dialCode={dialCode}
              onToggle={handleToggleFavourite}
              onAddNumber={handleAddNumber}
              onDone={() => setMobileSubStep('account')}
            />
          ) : null}
          {mobileSubStep === 'operator' ? (
            <OperatorStep
              operators={operators}
              selectedId={operatorId}
              onSelect={handleOperatorSelect}
            />
          ) : null}
          {mobileSubStep === 'details' ? (
            <FlowDetailsStep
              wallets={wallets}
              walletId={walletId}
              onWallet={setWalletId}
              currency={money}
              recipientName={recipientName}
              recipientAvatar={recipientAvatar}
              targetLabel={`${operatorById(operatorId ?? undefined)?.name ?? 'Mobile'} · ${dialCode} ${localNumber}`}
              onChangeRecipient={() => setMobileSubStep('account')}
              amount={amount}
              onAmount={setAmount}
              reason={reason}
              onReason={setReason}
              payee={payee}
              invoice={invoice}
              onCampaign={handleCampaign}
              fee={fee}
              totalDebit={totalDebit}
              error={targetError}
              covered={covered}
              shortfall={shortfall}
              canManage={canManage}
              ready={ready}
              onSubmit={handleSubmit}
            />
          ) : null}
        </>
      ) : null}

      {/* ---- Bank ---- */}
      {channel === 'bank' ? (
        <>
          {bankSubStep === 'recipient' ? (
            <BankRecipientStep
              payees={bankPayees}
              onMethod={handleBankMethod}
              onPickPayee={pickPayee}
            />
          ) : null}
          {bankSubStep === 'bank' ? (
            <BankPickerStep
              countryCode={countryCode}
              onCountry={handleCountrySelect}
              bankName={selectedBankName}
              onBank={setSelectedBankName}
              onContinue={handleBankContinue}
              canContinue={!!selectedBankName}
            />
          ) : null}
          {bankSubStep === 'account' ? (
            <BankAccountStep
              method={bankMethod ?? 'account'}
              account={bankAccountNumber}
              onAccount={setBankAccountNumber}
              onContinue={handleAccountContinue}
              canContinue={bankAccountNumber.length >= 6}
            />
          ) : null}
          {bankSubStep === 'details' ? (
            <FlowDetailsStep
              wallets={wallets}
              walletId={walletId}
              onWallet={setWalletId}
              currency={money}
              recipientName={recipientName}
              recipientAvatar={recipientAvatar}
              targetLabel={targetLabel}
              onChangeRecipient={() => setBankSubStep('recipient')}
              amount={amount}
              onAmount={setAmount}
              reason={reason}
              onReason={setReason}
              payee={payee}
              invoice={invoice}
              onCampaign={handleCampaign}
              fee={fee}
              totalDebit={totalDebit}
              error={targetError}
              covered={covered}
              shortfall={shortfall}
              canManage={canManage}
              ready={ready}
              onSubmit={handleSubmit}
            />
          ) : null}
        </>
      ) : null}

      {/* ---- Wallet ---- */}
      {channel === 'wallet' ? (
        <>
          {walletSubStep === 'account' ? (
            <WalletAccountStep
              value={walletValue}
              onChange={(value) => {
                setWalletValue(value);
                setWalletError('');
              }}
              payees={walletPayees}
              onPickPayee={pickPayee}
              onPickWallet={pickWallet}
              onContinue={() => void handleWalletContinue()}
              canContinue={walletValue.length >= 4}
              checking={walletChecking}
              error={walletError}
            />
          ) : null}
          {walletSubStep === 'details' ? (
            <FlowDetailsStep
              wallets={wallets}
              walletId={walletId}
              onWallet={setWalletId}
              currency={money}
              recipientName={recipientName}
              recipientAvatar={recipientAvatar}
              targetLabel={`Wallet ${walletValue}`}
              onChangeRecipient={() => setWalletSubStep('account')}
              amount={amount}
              onAmount={setAmount}
              reason={reason}
              onReason={setReason}
              payee={payee}
              invoice={invoice}
              onCampaign={handleCampaign}
              fee={fee}
              totalDebit={totalDebit}
              error={targetError}
              covered={covered}
              shortfall={shortfall}
              canManage={canManage}
              ready={ready}
              onSubmit={handleSubmit}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}